import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Brand, BrandVoice, BrandExtractionResult } from "../../../shared/types";

export function useBrands() {
  return useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<{ items: { id: string; data: Brand }[] }>("/api/brands"),
  });
}

export function useBrand(brandId: string | undefined) {
  return useQuery({
    queryKey: ["brands", brandId],
    queryFn: () => api.get<{ id: string; data: Brand }>(`/api/brands/${brandId}`),
    enabled: !!brandId,
  });
}

export function useBrandVoice(brandId: string | undefined) {
  return useQuery({
    queryKey: ["brands", brandId, "voice"],
    queryFn: () => api.get<{ id: string; data: BrandVoice }>(`/api/brands/${brandId}/voice`),
    enabled: !!brandId,
  });
}

export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Brand>) => api.post<{ id: string }>("/api/brands", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brands"] }),
  });
}

export function useUpdateBrand(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Brand>) => api.put<{ id: string }>(`/api/brands/${brandId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brands"] });
      qc.invalidateQueries({ queryKey: ["brands", brandId] });
    },
  });
}

export function useExtractBrand() {
  return useMutation({
    mutationFn: (url: string) =>
      api.post<BrandExtractionResult>("/api/brands/extract", { url }),
  });
}

export function useUpdateBrandVoice(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (voice: Partial<BrandVoice>) =>
      api.put<{ id: string }>(`/api/brands/${brandId}/voice`, voice),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brands", brandId, "voice"] }),
  });
}

export function useAddToCorpus(brandId: string) {
  return useMutation({
    mutationFn: (data: { content: string; metadata?: Record<string, unknown> }) =>
      api.post<{ id: string }>(`/api/brands/${brandId}/corpus`, data),
  });
}
